import { LitElement, html, css, nothing } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { StoppageAlarmReasons, OffTrackAlarmReasons, SpeedAlarmReasons, AlarmTypes, NotificationResponseState, SelectAlarmReason } from './type'
import '../../voyage-popup/src/voyage-popup';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { getAlarmDescription } from '../../../src/utils/vessel-alarm-utils';
import { UUID, GetUTCTimestamp, formatToTitleCase, GetFormattedDateDayMonthTimeNoYear } from '../../../src/utils/helpers';
import "@maersk-global/mds-components-core/mc-modal";
import { NotificationTypes, VoyNotification } from '../../../src/services/types/vessel-notifications-services.types';
import { AlarmEvents, VesselAlarmsModel } from '../../../src/models/vessel-alarms-model';
import { VesselAlertsModel, AlertType } from '../../../src/models/vessel-alerts-model';

const postponeDeadline = 1000 * 60 * 60 * 12; // 12 hours
const isVesselOnCloud: boolean = (window as any).appenv?.vesselOnCloud;

@customElement('voy-vessel-alarm-popup')
export class VoyVesselAlarmPopup extends LitElement {
    @property({ type: Boolean, reflect: true }) shown: boolean = false;

    @property({ type: StoppageAlarmReasons }) private _alarmReasons: StoppageAlarmReasons[] | SpeedAlarmReasons[] | OffTrackAlarmReasons[] = Object.values(StoppageAlarmReasons);
    @property({ type: false }) private _selectedReason: StoppageAlarmReasons | SpeedAlarmReasons | OffTrackAlarmReasons | SelectAlarmReason = StoppageAlarmReasons.SelectReasonForStoppage
    @property({ type: Array }) private _isSubmitted: boolean = false; 
    @property({ attribute: false }) private _comment: string | null = null;
    @property({ type: Array }) private _isSuccessPopup: boolean = false;
    @property({ type: String }) alarm: VoyNotification<NotificationTypes.Alarm>|null = null;
    @property({ type: String }) alarms: VoyNotification<NotificationTypes.Alarm>[]= [];
    @property({ type: Array }) private _showReasonsAndSuccessModal: boolean = true;
    @property({ type: Array }) private _isEditClicked: boolean = false;
    @property({ type: Boolean }) private hasActiveImminentRisk: boolean = false;

    constructor() {
        super()
        this._onSubmit = this._onSubmit.bind(this);
        this._onDoItLaterClick = this._onDoItLaterClick.bind(this);
        this._getAlarmReason = this._getAlarmReason.bind(this)
        this.updateEditOption = this.updateEditOption.bind(this)
    }

    updated(changedProperties: Map<string, any>) {
        super.updated(changedProperties);

        // If imminent risk is active, push alarm to background
        if (
            changedProperties.has('shown') &&
            this.shown &&
            this.hasActiveImminentRisk &&
            this.alarm
        ) {
            this._onDoItLaterClick();
        }
    }

    static get styles() {
        return [
            css`
            :host {
                font-family: MaerskText;
            }
            .comment {
                font-family: 'MaerskText';
                width: 380px;
                height: 70px;
                border: 1px solid #c4c4cd;
                line-height: 20px;
                resize: vertical;
                border-radius: 6px;
            }
            .comment[errorComment]{
                border-color: var(--voyage-alarm-red);
            }
            #alarm-popup .popup {
                width: 400px;
                max-width: 80vw;
                height: 40vh;
                padding:0 24px;
            }
            
            #success-topContainer{
                padding: 24px;
                width: 392px;
                display: grid;
                align-items: center;
                justify-items: start;
                column-gap: 15px;
                row-gap: 16px;
                border-bottom: 1px solid var(--neutrals-grey-07);
            }
            .success-innerContainer {
                display: flex;
                background-color: #C5E5C2;
                padding: 20px;
            }
            .content {
                margin-left: 20px;
            }
            .max-width-container h2 {
                margin-top: 0px;
            }
            .name {
                color: var(--neutrals-grey-02, grey);
                font-size: 16px;
                font-family: MaerskText;
                line-height:20px;
                font-weight: 400;
                margin-top: 20px;
                margin-bottom: 10px;
            }
            .desc {
                color: var(--neutrals-grey-02, grey);
                font-size: 14px;
                font-family: MaerskText;
                line-height:20px;
                margin-top: -10px;
            }
            .desc span {
                text-decoration: underline;
                cursor: pointer;
            }
            .dropdown {
                box-sizing: border-box;
                min-height: 41px;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 2.5px;
                font-family: MaerskText;
                font-size: 14px;
                color: var(--neutrals-grey-02, black);
                background-color: var(--neutrals-grey-10, white);
                text-indent: 5px;
                width: 100%;
                width: 394px;
                margin-bottom: 10px;
                border-radius: 6px;
            }

            .dropdown[disabled] {
                background-color: var(--neutrals-grey-08, black);
            }
            .dropdown[error] {
                border-color: #B80012;
            }
            #alarm-popup #footer {
                display: flex;
                justify-content: flex-end;
                padding: 12px;
                background: #F7F7F7;
            }
            
            .title {
                margin-top: 0px;
                font-family: MaerskText;
            }
            
            #alarm-popup #footer button {
                height: 35px;
                min-width: 80px;
                font-size: 14px;
                font-family: MaerskText;
                background: none;
                border: none;
                outline: none;
                cursor: pointer;
                border-radius: 3px;
                width: auto;
            }

            #alarm-popup #footer .do-it-later-button {
                color: var(--neutrals-grey-02);
                margin-right: 10px;
                border: 1px solid var(--neutrals-grey-06, grey);
                display: flex;
                line-height: 28px;
            }

            #alarm-popup #footer .submit-button {
                margin-right: 20px;
                border-radius: 6px;
            }

            .comment-input{
                width: 100%;
                position: realtive;
            }

            .max-width-container {
                width: 100%;
            }
            .center {
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100%;
            }
            .error-message {
                color: #B80012;
                line-height: 20px;
                font-size: 14px;
                font-weight: 400;
                margin-top: -5px;

            }
            .comment-input > textarea {
                white-space: normal;
                padding-left: 10px;
                font-family: MaerskText;
            }
           .footer {
            text-align: end;
            padding: 16px 24px;
            margin: -24px;
            margin-top: 20px;
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            align-items: center;
            padding: 16px 24px;
            gap: 12px;
            height: 64px;
            background: #F7F7F7;
            border-bottom-left-radius: 6px;
            border-bottom-right-radius: 6px;
           }
           .footer-button {
            background-color: #FFFFFF;
           }
        `];
    }
    connectedCallback() {
        super.connectedCallback();
        VesselAlarmsModel!.addEventListener(AlarmEvents.AlarmsEdited, this.updateEditOption);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
		VesselAlarmsModel!.removeEventListener(AlarmEvents.AlarmsEdited, this.updateEditOption);
    }

    public updateEditOption(){
        this._isEditClicked = true
    }
    render() {
        // Ensure alarms exist before showing the modal.
        if (this.alarms.length > 0) {
            if (!this.alarm) {
                return
            }

            this._showReasonsAndSuccessModal = true;


            return html`
            ${this._showReasonsAndSuccessModal ? 
                (this._isSuccessPopup ? 
                    this._renderSubmitSuccessModal() : 
                    this._renderReasonSubmitModal()
                ) 
                : nothing
            }`;
        } 
        return nothing;
    }

    private _renderReasonSubmitModal() {
		if(!this.alarm) {
			throw Error("alarm is undefined")
		}
        
        const {data: alarmData} = this.alarm;
        const title = (alarmData.alarmType.toLocaleLowerCase() == AlarmTypes.Crosstrack)? "offtrack": alarmData.alarmType;
        const isFleetUser= sessionStorage.getItem('isVesselUser') === 'No';
        const showDoItLaterButton = isVesselOnCloud && isFleetUser 

        return html`<mc-modal
                heading= ${`${formatToTitleCase(title)} | ${GetFormattedDateDayMonthTimeNoYear(this.alarm?.created_at)} UTC`}
                open=${!this.shown}
                dimension="small"
                width="443px"
                height="467px"
                hiddenfooter
                backdropcloseactiondisabled	
                escapecloseactiondisabled	
                entercloseactiondisabled
                hiddenclose
                >
					${this._renderModalContent()}
					<div class = "footer">
						${((new Date().getTime() - new Date(this.alarm.created_at).getTime() < postponeDeadline)  ||  showDoItLaterButton)? html`<mc-button class = "footer-button" focusendanchor  appearance="neutral" variant="outlined"  @click="${this._isEditClicked? this._onCancel: this._onDoItLaterClick}" >${this._isEditClicked ? "Cancel" : "Do it Later"}</mc-button>` : nothing}
						<mc-button focusstartanchor  appearance="secondary" variant="filled" @click="${this._onSubmit}" >Submit</mc-button>
					</div>
            </mc-modal>
        `
    }

    private _renderSubmitSuccessModal() {
        return html`
            <voyage-popup id="alarm-popup" .noCloseButton = ${true} .noHeader = ${true}
                ?shown=${ this.shown || this._isEditClicked} >
                <div id="success-topContainer">
                    <div class="max-width-container">
                        <div class="success-innerContainer">
                            <div>
                                <span>${SvgIcons.tickMark}</span>
                            </div>
                            <div class="content">
                                <h4 class ="title" > Thank you!</h4>
                                <div class="desc">Your input has been sent to Voyage Execution Center.
                                <p>Please submit an updated Voyage plan.</p>
                                <p>You can see the <span @click =${() => this._openLog()}> Alarm Log</span> in Settings.</p>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>
                    <div id="footer" slot="footer">
                         <mc-button  class="submit-button"  appearance="secondary" variant="filled" @click=${() => this._onClose()} >Close</mc-button>
                 </div>
            </voyage-popup>
        `
    }

    private _renderModalContent() {
		if(!this.alarm) {
			return
		}
        let modalData = getAlarmDescription(this.alarm);
        this._alarmReasons = this._getAlarmReason()
        return html`
                    <div class="desc">${modalData.alarmDesc}
                         <br/>${modalData.alarmsubDesc}.
                    </div>
                    <div class="name">Reason</div>
                    <select class="dropdown" 
                        .value=${this._selectedReason} 
                        ?disabled=${this._alarmReasons?.length == 0} 
                        @input=${(event: { target: { value: StoppageAlarmReasons | SpeedAlarmReasons | OffTrackAlarmReasons }}) => this._handleInputReason(event)}
                        ?error=${this._checkForSelectedOption() && this._isSubmitted}
                     >
                         ${(this._alarmReasons as Array<StoppageAlarmReasons | SpeedAlarmReasons | OffTrackAlarmReasons>).map((val: string) => html`
                         <option .value= ${val}>${val}</option>
                        `)}
                     </select>
                     ${this._checkForSelectedOption() && this._isSubmitted ? html`<div class = "error-message" >Please select reason</div>` : html``}
                <div class="comment-input">
                    <div class="name">Comment ${this._isCommentOptional() ? html`<span>(optional)</span> ` : html` `}</div>
                    <textarea class="comment" type="text"  placeholder="Write your reason or notes"   ?errorComment = ${this._checkForCommentOptional()}  @input = ${(evt: { target: { value: string; }; }) => this._comment = evt.target.value.trim()}></textarea> 
                    ${this._checkForCommentOptional() ? html`<div class = "error-message" >Please provide a comment</div>` : html``}

                </div>
        `
    }

    private _handleInputReason(evt: { target: { value: StoppageAlarmReasons | SpeedAlarmReasons | OffTrackAlarmReasons }}){
        this._selectedReason = evt.target.value
    }

    private _checkForSelectedOption() {
        switch (this._selectedReason) {
            case SelectAlarmReason.SelectReasonForOffTrack:
            case SelectAlarmReason.SelectReasonForSpeedDeviation:
            case SelectAlarmReason.SelectReasonForStoppage:
                return true;
            default: return false
        }
    }

    private _checkForCommentOptional() {
        return (!this._isCommentOptional() && (this._comment === null || this._comment === "") && this._isSubmitted);
    }

    private _onDoItLaterClick() {
		if(!this.alarm){
			return
		}
        this._isSubmitted = false;
        this._isEditClicked = false;
        const title = (this.alarm.data.alarmType.toLocaleLowerCase() == AlarmTypes.Crosstrack)? "offtrack": this.alarm.data.alarmType;
        VesselAlertsModel!.addAlert((<any>window)["appenv"].vesselIMONumber, {
            message: getAlarmDescription(this.alarm!).alarmDesc,
            id: this.alarm!.id,
            title: title,
            type: AlertType.AlarmReminder,
            time: this.alarm?.created_at
        });
        if (this.alarm?.id) {
            const pendingAlarms = JSON.parse(localStorage.getItem("doItLaterAlarm") || "[]");
            localStorage.setItem("doItLaterAlarm", JSON.stringify([...pendingAlarms, this.alarm]));
        }
        this.alarms.shift()
        this.dispatchEvent(new CustomEvent(AlarmEvents.CheckMoreAlarms, { detail: this.alarms }));
        this._showReasonsAndSuccessModal = false;
        VesselAlarmsModel?.checkForMoreAlarms()
    }
   private _onCancel(){
        this.alarms.shift()
        this._isSubmitted = false;
        this._isEditClicked = false;
        this.dispatchEvent(new CustomEvent(AlarmEvents.CheckMoreAlarms, { detail: this.alarms }));
        this._showReasonsAndSuccessModal = false;
        VesselAlarmsModel?.checkForMoreAlarms()
   }

    private _onSubmit() {
        this._isSubmitted = true;
        if(!this.alarm){
            return
        }
        if (this._formValid()) {
            VesselAlarmsModel?.postAlarmsReasons(this._payloadForSubmitReason(), this.alarm!.id);
             //remove this item from localstorage doitLaterAlarm if existing
            let pendingAlarm = JSON.parse(localStorage.getItem("doItLaterAlarm") || "[]") as VoyNotification<NotificationTypes.Alarm>[];
            let indexOfItem = this.findIndexOf(pendingAlarm, this.alarm)
            if (indexOfItem >= 0) localStorage.setItem("doItLaterAlarm", JSON.stringify([...pendingAlarm.slice(0, indexOfItem), ...pendingAlarm.slice(indexOfItem + 1)]))
            if(this._isEditClicked){
                this._onClose()
                this._isSuccessPopup = false;
            } 
            else
            {
                this._isSuccessPopup = true;
            }
            
        }
    }

    private _payloadForSubmitReason() {
        return {
            state: NotificationResponseState.ActiveState,
            // data is nested json, so have to stringify this prop
            data: JSON.stringify({
                reason: this._selectedReason,
                comments: this._comment
            })
        }
    }

    private _onClose() {
		if(!this.alarm){
			return
		}
        this._isEditClicked = false;
        let alarmdata = this.alarm.data
        const title = (alarmdata.alarmType.toLocaleLowerCase() == AlarmTypes.Crosstrack)? "offtrack": alarmdata.alarmType;
        VesselAlertsModel!.addAlert((<any>window)["appenv"].vesselIMONumber, {
            message: "The alarm will be active until vessel has been below alarm limits for 1 consecutive hour.",
            id: this.alarm!.id,
            title: title,
            type: AlertType.SubmittedAlarm,
            time: this.alarm?.created_at
        });

        this.alarms.shift()
        this._showReasonsAndSuccessModal = false;
        this._isSubmitted = false;
        this._comment = null;
        this._isSuccessPopup = false;
        this._selectedReason = StoppageAlarmReasons.SelectReasonForStoppage
        this.dispatchEvent(new CustomEvent(AlarmEvents.CheckMoreAlarms, { detail: this.alarms }));
    }

    private _isCommentOptional() {
        //Comment field is mandatory for Technical issue and others options
        switch (this._selectedReason) {
            case StoppageAlarmReasons.TechnicalIssue:
            case StoppageAlarmReasons.Other:
            case SpeedAlarmReasons.Other:
            case SpeedAlarmReasons.TechnicalIssue:
            case OffTrackAlarmReasons.Other:
            case StoppageAlarmReasons.InaccurateAlarm:
            case SpeedAlarmReasons.InaccurateAlarm:
            case OffTrackAlarmReasons.InaccurateAlarm:
            case StoppageAlarmReasons.ChangeEtaorDestination:
            case OffTrackAlarmReasons.Drifting:
            case OffTrackAlarmReasons.DenseTrafficManoeuvring:
            case OffTrackAlarmReasons.ChangeEtaorDestination:
            case OffTrackAlarmReasons.NavigationWarning:
                return false;
            default: return true;
        }


    }

    private _formValid() {
        if (this._selectedReason !== StoppageAlarmReasons.SelectReasonForStoppage && this._selectedReason !== SpeedAlarmReasons.SelectReasonForSpeed && this._selectedReason !== OffTrackAlarmReasons.SelectReasonForOfftrack) {
            if (this._isCommentOptional()) return true;
            else {
                if (this._comment !== "" && this._comment !== null) return true
            }
        }
        return false
    }

    private _getAlarmReason() {
        let alarmData = (this.alarm!.data);
        let type: string = alarmData.alarmType.toLocaleLowerCase();

        switch (type) {
            case "stoppage": return Object.values(StoppageAlarmReasons);
            case "speed": return Object.values(SpeedAlarmReasons);
            case "crosstrack": return Object.values(OffTrackAlarmReasons);
            default: return Object.values(StoppageAlarmReasons);
        }
    }

    private findIndexOf(array: VoyNotification<NotificationTypes.Alarm>[], object: VoyNotification<NotificationTypes.Alarm>) {
        for (let i = 0; i < array.length; i++) {
            if (array[i].id == object.id) {
                return i;
            }
        }
        return -1; // Return -1 if object is not found in the array
    }
    private _openLog(){
        VesselAlarmsModel?.openLog();
        this._onClose()
    }
}
